"use client";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { CountBadge } from "@/components/ui/count-badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  MenuIcon,
  NotificationIcon,
  SearchIcon,
} from "@/components/ui/icons";
import { defaultNavigationLinks, type NavbarNavLink } from "./nav-links";
import { SettingsPopover } from "./settings/settings-dialog";
import { useSelector } from "react-redux";
import { TotalNotificationsCount } from "./types";
import { SearchDialog } from "./settings/search-dialog";
import { NotificationDialog } from "./settings/notifications-dialog";
import { MenuDrawer } from "./menu-drawer";
import { useNotificactionStore } from "../Mail/store/notitication-store";
import { useSession } from "@/providers/session-provider";

// Types
export type { NavbarNavLink };
export interface NavbarProps extends React.HTMLAttributes<HTMLElement> {
  logo?: React.ReactNode;
  logoHref?: string;
  navigationLinks?: NavbarNavLink[];
}

export const Navbar = React.forwardRef<HTMLElement, NavbarProps>(
  (
    {
      className,
      logoHref = "/home",
      navigationLinks = defaultNavigationLinks,
      ...props
    },
    ref,
  ) => {
    const location = useLocation();
    const currentPath = "" + location.pathname;
    const [deviceCtrl, setDeviceCtrl] = useState<string | null>(null);
    const { fetchUnreads, unreads } = useNotificactionStore();
    const { session } = useSession();

    const getDeviceCtrl = async () => {
      if (navigator.userAgent) {
        if (navigator.userAgent.includes("Mac")) {
          setDeviceCtrl("⌘");
        } else {
          setDeviceCtrl("CTRL");
        }
      }
    };

    useEffect(() => {
      getDeviceCtrl();
      fetchUnreads();
    }, []);

    const { totalNotificationsCount } = useSelector(
      (s: { profile: { totalNotificationsCount: TotalNotificationsCount } }) =>
        s.profile,
    );

    // Bell count — unread outreach replies (GET mail/has-unreads), falling
    // back to the profile notification total. No placeholder: the badge must
    // only ever show a number the dropdown can actually account for.
    const notificationCount =
      unreads || totalNotificationsCount.total_count || 0;

    // The Inbox lives in Outreach's first tab, so the unread-mail count
    // (GET mail/has-unreads) badges the Outreach nav item. An explicitly
    // passed badge still wins.
    const links = navigationLinks.map((link) =>
      link.href === "/outreach" && link.badge === undefined
        ? { ...link, badge: unreads }
        : link,
    );

    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
          e.preventDefault();
          setSearchOpen(true);
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, []);

    const initials = (
      session?.first_name?.[0] ||
      session?.email?.[0] ||
      "U"
    ).toUpperCase();

    return (
      <>
        <header
          ref={ref}
          className={cn(
            "sticky top-0 z-50 w-full border-b bg-background backdrop-blur px-4 py-1.5 **:no-underline",
            className,
          )}
          {...props}
        >
          <div className="relative flex h-12 w-full items-center justify-between gap-4">
            {/* ------------------------------------------------------------
              Left: mobile menu + logo + LinkedIn-style search button
              ------------------------------------------------------------ */}
            <div className="flex items-center gap-2">
              <div className="xl:hidden">
                <MenuDrawer>
                  <Button variant="ghost" size="icon-sm">
                    <HugeiconsIcon icon={MenuIcon} />
                  </Button>
                </MenuDrawer>
              </div>

              <a
                href={logoHref}
                className="flex items-center transition-opacity hover:opacity-80"
                aria-label="Flozy"
              >
                <img src={"/logo.png"} alt="Flozy" className="size-8" />
              </a>

              {/* Search pill — full pill with label + ⌘K, absolutely centered.
                  Shown only when the nav is the hamburger (md/lg); below md the
                  floating FAB takes over, and at xl+ the icon button by the
                  notification bell does. */}
              <SearchDialog open={searchOpen} onOpenChange={setSearchOpen}>
                <button
                  type="button"
                  className={cn(
                    "hidden md:flex xl:hidden items-center h-9 w-xs gap-2 px-3 rounded-full bg-transparent border border-[var(--border-strong)] hover:bg-muted/40 transition-colors text-sm text-muted-foreground cursor-pointer",
                    "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklch,var(--primary)_22%,transparent)]",
                    // absolutely centered — the nav is the hamburger at these
                    // sizes, so the middle is free
                    "md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
                  )}
                >
                  <HugeiconsIcon
                    icon={SearchIcon}
                    className="size-4 shrink-0"
                  />
                  <span className="flex-1 truncate text-left">Search</span>
                  <kbd className="inline-flex items-center gap-1 shrink-0 rounded-md border border-[var(--border-strong)] bg-background px-1.5 h-5 font-sans text-[12px] font-medium text-muted-foreground">
                    <span className="text-[14px] leading-none">
                      {deviceCtrl || "⌘"}
                    </span>
                    <span>K</span>
                  </kbd>
                </button>
              </SearchDialog>
            </div>

            {/* ------------------------------------------------------------
              Center: icon-over-label nav with primary-blue underline
              Absolutely centered to the viewport (not balanced between
              left and right groups) so it sits perfectly in the middle.
              ------------------------------------------------------------ */}
            <nav className="hidden xl:flex items-stretch h-full absolute left-1/2 -translate-x-1/2">
              <ul className="flex items-stretch h-full gap-1">
                {links.map((link) => {
                  const isActive = currentPath.startsWith(link.href);
                  return (
                    <li key={link.href} className="flex">
                      <a
                        href={link.href}
                        aria-current={isActive ? "page" : undefined}
                        className={cn(
                          "group relative flex flex-col items-center justify-center gap-0.5 px-4 min-w-[64px] h-12 text-[12px] font-medium leading-none transition-colors no-underline",
                          "focus-visible:outline-none focus-visible:bg-muted/60 rounded-md",
                          isActive
                            ? "text-primary"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        <span className="relative">
                          <HugeiconsIcon
                            icon={link.icon}
                            size={22}
                            strokeWidth={1.75}
                          />
                          <CountBadge
                            count={link.badge ?? 0}
                            className="absolute -top-1.5 -right-2.5"
                          />
                        </span>
                        <span>{link.label}</span>
                        {/* LinkedIn-style underline slider — primary blue when active */}
                        <span
                          aria-hidden
                          className={cn(
                            "pointer-events-none absolute left-2 right-2 -bottom-[7px] h-[2px] rounded-full transition-all duration-200",
                            isActive
                              ? "bg-primary opacity-100"
                              : "bg-transparent opacity-0",
                          )}
                        />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* ------------------------------------------------------------
              Right: notifications + profile avatar (settings trigger)
              ------------------------------------------------------------ */}
            <div className="flex items-center gap-1.5">
              {/* xl+ : search collapses to an icon button, right beside the
                  notification bell (centered pill / FAB cover smaller widths). */}
              <Button
                size="icon"
                variant="ghost"
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
                className="hidden xl:inline-flex hover:cursor-pointer"
              >
                <HugeiconsIcon
                  icon={SearchIcon}
                  strokeWidth={1.75}
                  className="size-[22px]"
                />
              </Button>

              <NotificationDialog>
                <Button
                  size="icon"
                  className="relative hover:cursor-pointer"
                  variant="ghost"
                  aria-label="Notifications"
                >
                  <HugeiconsIcon
                    icon={NotificationIcon}
                    strokeWidth={1.75}
                    className="size-[22px]"
                  />
                  <CountBadge
                    count={notificationCount}
                    className="absolute -top-0.5 -right-0.5"
                  />
                </Button>
              </NotificationDialog>

              <SettingsPopover>
                <button
                  type="button"
                  className="ml-1 rounded-full outline-none transition-shadow focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklch,var(--primary)_22%,transparent)] hover:opacity-90 cursor-pointer"
                  aria-label="Profile and settings"
                >
                  <Avatar className="size-8">
                    <AvatarImage
                      src={session?.avatar_filename || ""}
                      alt={session?.first_name || session?.email || "Profile"}
                    />
                    <AvatarFallback tone="blue">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </SettingsPopover>
            </div>
          </div>
        </header>

        {/* Stage 4 (<md): search leaves the header and floats above the
            support bubble — opens the same ⌘K command palette. */}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
          title="Search"
          className="fixed bottom-[88px] right-6 z-50 md:hidden inline-flex items-center justify-center size-12 rounded-full bg-background border border-[var(--border-strong)] text-foreground shadow-lg transition-all hover:bg-muted/40 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <HugeiconsIcon icon={SearchIcon} size={22} />
        </button>
      </>
    );
  },
);
Navbar.displayName = "Navbar";
